import type { ActivityOf } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, type MinigameResult } from './types.ts';

/** Canvas units: the pointer arrives in these. */
export const SCRIPT_W = 340;
export const SCRIPT_H = 240;
const GUIDE_POINTS = 160;
/** Seconds to trace a flourish once the pen touches down. */
export const STROKE_LIMIT = 7;
/** Seconds to put pen to the green mark before the flourish is spoiled. */
const START_LIMIT = 20;
const PAUSE_TICKS = Math.round(0.9 * TICK_HZ);
/** How close to the green mark the pen must land. */
export const START_RADIUS = 24;

export interface ScriptPoint {
  x: number;
  y: number;
}

export interface StrokeScore {
  v: number;
  cov: number;
  accu: number;
}

/** The pointer is the pen; BTN.main is the pen on the page. */
export interface IlluminationSim extends RealtimeSim {
  phase: 'trace' | 'pause';
  stroke: number;
  strokes: number;
  /** The gilded guide for this flourish. */
  guide: ScriptPoint[];
  covered: boolean[];
  /** The ink laid down on this flourish. */
  ink: ScriptPoint[];
  /** The pen is down and tracing. */
  active: boolean;
  /** The pen has touched down on this flourish (it may since have lifted). */
  started: boolean;
  /** Seconds left on this flourish once started. */
  timeLeft: number;
  /** Distance from the pen to the guide on the last move. */
  lastDistance: number;
  tolerance: number;
  scores: StrokeScore[];
}

/**
 * The Scriptorium. Trace each flourish with a steady hand: begin at the green mark and follow the
 * gilded guide without lifting. Ported from the Harrowmere prototype.
 */
export function illuminationSim(a: ActivityOf<'illumination'>, level: number, seed: string): IlluminationSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  // Only practice beyond the page steadies the hand: each tier's own tolerance is what it asks.
  const tol = a.tolerance + Math.max(0, level - a.level) * 0.1;
  let prev = 0, sumD = 0, n = 0, waited = 0, traced = 0, pauseLeft = 0;
  let last: ScriptPoint | null = null;

  const fx = (text: string, tone: Fx['tone'], cue?: string, power?: number) => sim.fx.push({ tick: sim.tick, text, tone, cue, power });

  const newGuide = (): ScriptPoint[] => {
    const A1 = rand(30, 60), f1 = rand(0.8, 1.6), p1 = rand(0, 6.28), A2 = rand(8, 22), f2 = rand(2, 3.5), p2 = rand(0, 6.28);
    const flip = rng.next() < 0.5;
    return Array.from({ length: GUIDE_POINTS }, (_, i) => {
      const t = i / (GUIDE_POINTS - 1);
      return {
        x: 30 + 280 * (flip ? 1 - t : t),
        y: clamp(120 + A1 * Math.sin(6.283 * f1 * t + p1) + A2 * Math.sin(6.283 * f2 * t + p2), 22, SCRIPT_H - 22),
      };
    });
  };

  const beginStroke = () => {
    sim.phase = 'trace';
    sim.guide = newGuide();
    sim.covered = sim.guide.map(() => false);
    sim.ink = [];
    sim.active = false;
    sim.started = false;
    sim.timeLeft = STROKE_LIMIT;
    sumD = 0;
    n = 0;
    waited = 0;
    traced = 0;
    last = null;
  };

  const endStroke = () => {
    sim.active = false;
    const cov = sim.covered.filter(Boolean).length / sim.covered.length;
    const accu = n ? clamp(1 - sumD / n / (tol * 1.6), 0, 1) : 0;
    const v = (cov * 0.55 + accu * 0.45) * (cov < 0.5 ? 0.5 : 1);
    sim.scores.push({ v, cov, accu });
    fx(v > 0.85 ? 'Beautiful!' : v > 0.6 ? 'A fair hand' : 'Shaky…', v > 0.6 ? 'good' : 'bad', 'stroke', v);
    if (sim.scores.length >= a.strokes) {
      const avg = (k: keyof StrokeScore) => sim.scores.reduce((s, x) => s + x[k], 0) / sim.scores.length;
      const score = avg('v');
      sim.result = {
        score,
        title: score > 0.85 ? 'A page fit for a king' : score > 0.6 ? 'A fair copy' : 'A blotted page',
        text: `You finish ${a.strokes} flourish${a.strokes === 1 ? '' : 'es'} on the ${a.name.toLowerCase()}.`,
        breakdown: [['Coverage', avg('cov')], ['Steadiness', avg('accu')]],
        yield: { itemKey: a.product, quantity: 1, graded: true },
        coin: 0,
        xpMult: 1,
        feats: sim.scores.every((x) => x.v > 0.85) ? ['flawless-hand'] : [],
      } satisfies MinigameResult;
      return;
    }
    sim.phase = 'pause';
    pauseLeft = PAUSE_TICKS;
  };

  const sim: IlluminationSim = {
    tick: 0,
    result: null,
    fx: [],
    phase: 'trace',
    stroke: 0,
    strokes: a.strokes,
    guide: [],
    covered: [],
    ink: [],
    active: false,
    started: false,
    timeLeft: STROKE_LIMIT,
    lastDistance: 0,
    tolerance: tol,
    scores: [],
    step(mask, x, y) {
      if (sim.result) return;
      sim.tick++;
      const down = (mask & BTN.main) !== 0;
      const pressed = down && (prev & BTN.main) === 0;
      prev = mask;

      if (sim.phase === 'pause') {
        if (--pauseLeft <= 0) {
          sim.stroke++;
          beginStroke();
        }
        return;
      }

      if (!sim.started) {
        waited++;
        if (pressed) {
          const g0 = sim.guide[0]!;
          if (Math.hypot(x - g0.x, y - g0.y) > START_RADIUS) fx('Begin at the green mark', 'muted', 'misplaced');
          else {
            sim.started = sim.active = true;
            last = { x, y };
            sim.ink.push({ x, y });
            return;
          }
        }
        if (waited >= START_LIMIT * TICK_HZ) {
          fx('The ink dries on your quill', 'bad', 'dried');
          endStroke();
        }
        return;
      }

      if (!sim.active) return;
      traced++;
      sim.timeLeft = STROKE_LIMIT - traced / TICK_HZ;
      if (!down) return endStroke();
      // Only movement is judged, as in the prototype, where every pointer move was scored.
      if (!last || last.x !== x || last.y !== y) {
        let best = Infinity;
        for (let i = 0; i < sim.guide.length; i++) {
          const p = sim.guide[i]!;
          const d = Math.hypot(p.x - x, p.y - y);
          if (d < best) best = d;
          if (d <= tol * 1.3) sim.covered[i] = true;
        }
        sumD += best;
        n++;
        sim.lastDistance = best;
        sim.ink.push({ x, y });
        last = { x, y };
      }
      if (sim.timeLeft <= 0) endStroke();
    },
  };
  beginStroke();
  return sim;
}
